const EventEmitter = require('events')
const {ulid} = require('ulid')
const {debounce} = require('./functions')
const ChatterService = require('./chatter-service')

var logger = {
  error: (...args) => {console.error(...args)},
  warn: (...args) => {console.warn(...args)},
  info: (...args) => {console.info(...args)},
  debug: (...args) => {console.debug(...args)},
  verbose: (...args) => {console.debug(...args)}
}

const events = {
  ONLINE: 'online',
  OFFLINE: 'offline',
  CHAT_STATE: 'chat-state',
  CHAT_MESSAGE: 'chat-message',
  CHAT_HISTORY: 'chat-history',
  ERROR: 'error'
}

const CHAT_STATES = {
  ACTIVE: 'active',
  PAUSED: 'paused',
  COMPOSING: 'composing',
  INACTIVE: 'inactive',
  GONE: 'gone'
}

const actions = {
  MESSAGE: 'onMessage',
};

const bindEvents = (chatterInstance) => {
  chatterInstance._wsClient.onopen = (event) => {
    chatterInstance.emit(events.ONLINE)
  };

  chatterInstance._wsClient.onerror = (event) => {
    logger.error('E', event);
    chatterInstance.emit(events.ERROR, event);
  };

  chatterInstance._wsClient.onmessage = (event) => {
    const message = event.data;
    handleMessage(message, chatterInstance)
  };
}

const handleMessage = function(message, chatterInstance) {
  console.debug('Message received: ', message);
  const temp = JSON.parse(message);
  
  if (temp && temp.payload && temp.payload.value) {
    chatterInstance.emit(events.CHAT_MESSAGE, { body: temp.payload.value, sentAt: temp.createdAt || '' });
    return;
  }

  if (temp && temp.state) {
    chatterInstance.emit(events.CHAT_STATE, { state: temp.state || '' });
    return;
  }
}

class Chatter extends EventEmitter {
  constructor({ chatterService }) {
    super()
    this.chatterService = chatterService
    this.websocket = chatterService.api.websocket
  }

  getNewCredentials({} = {}) {
    return this.chatterService.getNewCredentials()
  }

  connect({ user }) {
    this.user = user;

    this._wsClient = new WebSocket(`${this.websocket}?user=${this.user}&channel=${this.chatterService.channelId}&token=${this.chatterService.token}`);

    bindEvents(this);
    return this;
  }

  disconnect() {
    this._wsClient.close();
    return this;
  }

  async getHistory({ pageSize = 10, beforeTimestamp }) {
    if (typeof beforeTimestamp === 'undefined') {
      beforeTimestamp = (new Date()).getTime()
    }
    if (beforeTimestamp.toString().length === 13) {
      beforeTimestamp *= 1000
    }

    const history = await this.chatterService.getHistory(this.user);
    let r = {
      complete: true,
      messages: history.sort((a, b) => a.timestamp < b.timestamp)
    }
    
    this.emit(events.CHAT_HISTORY, r)
  }

  async sendText({ id, text }) {
    try {
      if (typeof text === 'undefined' || text === '') return;
      if (typeof id === 'undefined') {
        id = ulid();
      }
      
      const payload = {
        channelId: this.chatterService.channelId,
        token: this.chatterService.token,
        user: this.user,
        text,
      };

      if (this.typing) this.sendTypingStop();
      await this._wsClient.send(JSON.stringify({ action: actions.MESSAGE, data: payload }));
      return {
        id, sent: true, body: text,
      };
    } catch (error) {
      throw error;
    }
  }

  async uploadAndSendAttachment({ file }) {
    try {
      let res = await this.chatterService.uploadFile(file)
      let { url } = res

      const payload = {
        channelId: this.chatterService.channelId,
        token: this.chatterService.token,
        user: this.user,
        type: 'attachment',
        url,
        contentType: file.type,
      };

      if (this.typing) this.sendTypingStop();
      await this._wsClient.send(JSON.stringify({ action: actions.MESSAGE, data: payload }));

      let attachment = {
        contentType: file.type,
        url: url
      }

      return {
        sent: true, body: file.name, attachment
      }
    } catch (error) {
      throw error
    }
  }

  async sendAttachment({ attachment: { contentType, url } }) {
    try {
      return {
        sent: true, body: url, attachment
      }
    } catch (error) {
      throw error
    }
  }
}

const chatter = {
  async initialize({ url, websocket, storage, channelId, token }, { logger: customLogger = null } = {}) {
    try {
      if (customLogger) logger = customLogger
      const chatterService = new ChatterService({ url, websocket, storage, channelId, token })
      return new Chatter({ chatterService })
    } catch (error) {
      throw error
    }
  },
  events,
  CHAT_STATES
}

if (typeof window !== 'undefined') {
  window.chatter = chatter
}
module.exports = chatter
